import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  sectionPadding,
  pageTopPadding,
  CONTENT_MAX_WIDE,
  CONTENT_MAX_NARROW,
  CONTENT_INSET,
} from '../styles/layout';

const GOLD = '#C9A84C';
const BG = '#090909';
const CARD_BG = '#111111';
const TEXT_MUTED = '#F5F0E8';
const TEXT_BODY = '#F5F0E8';
const GOLD_BORDER = '1px solid rgba(201, 168, 76, 0.2)';

const sections = [
  {
    title: 'Service Description',
    content: `Digital Divot provides premium mobile golf simulator rentals throughout the Phoenix and Scottsdale metropolitan area in Arizona. Our state-of-the-art bays feature Trackman technology and access to 200+ world-class courses. Services are delivered to your venue — home, office, event space, or corporate function — subject to site requirements (adequate ceiling height, flat surface, power access, and vehicle access).`,
  },
  {
    title: 'Booking & Cancellation Policy',
    content: `All bookings require a 25% deposit at the time of reservation. The remaining balance is charged automatically to the card on file one day before your scheduled event. Cancellations made at least 48 hours before your scheduled start time are eligible for a full refund of the deposit. Cancellations made within 48 hours of the scheduled start time may be subject to a cancellation fee of up to 10% of the total booking value. To cancel, contact us at (480) 923-3349 or admin@digitaldivotgolf.com with your confirmation code.`,
  },
  {
    title: 'Deposits & Refunds',
    content: `Deposits are processed securely through Stripe at the time of booking. If Digital Divot cancels your booking due to equipment failure or scheduling conflict on our end, you will receive a full refund of all amounts paid. Refunds are processed to the original payment method within 5–10 business days. Partial refunds for cancellations within the 48-hour window are handled on a case-by-case basis at Digital Divot's discretion.`,
  },
  {
    title: 'Damage Liability',
    content: `The customer is responsible for providing a safe, accessible setup area and for any damage to Digital Divot equipment caused by negligence, misuse, unauthorized handling, or failure to meet disclosed venue requirements. Normal wear from intended use is not charged. If equipment is damaged beyond normal use, the customer may be charged for repair or replacement costs. Digital Divot carries commercial liability insurance; however, customers are responsible for damage to their own property or venue caused during setup, operation, or teardown except where caused by Digital Divot negligence.`,
  },
  {
    title: 'Weather & Force Majeure',
    content: `Outdoor bookings are subject to weather conditions. Digital Divot reserves the right to reschedule or cancel outdoor events due to rain, high winds, extreme heat, or other unsafe conditions without penalty to the customer. In such cases, your deposit will be applied to a rescheduled date or refunded in full. Neither party shall be liable for failure to perform due to events beyond reasonable control, including natural disasters, government orders, or utility outages.`,
  },
  {
    title: 'Disputes & Contact',
    content: `For questions, cancellations, or disputes regarding your booking, contact Digital Divot at (480) 923-3349 or admin@digitaldivotgolf.com. We aim to respond to all inquiries within one business day. These Terms of Service are governed by the laws of the State of Arizona.`,
  },
];

const Terms = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div
      style={{
        paddingTop: pageTopPadding(isMobile),
        backgroundColor: BG,
        color: '#F5F0E8',
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Helmet>
        <title>Terms of Service | Digital Divot Golf</title>
        <meta
          name="description"
          content="Digital Divot Golf terms of service — booking, cancellation, deposits, liability, and dispute policies for mobile simulator rentals."
        />
        <meta property="og:title" content="Terms of Service | Digital Divot Golf" />
        <meta property="og:url" content="https://digitaldivotgolf.com/terms" />
      </Helmet>
      <section style={{ ...sectionPadding(isMobile), paddingBottom: '4rem' }}>
        <div style={{ maxWidth: CONTENT_MAX_WIDE, margin: '0 auto', ...CONTENT_INSET }}>
          <div style={{ maxWidth: CONTENT_MAX_NARROW, margin: '0 auto' }}>
            <h1
              style={{
                fontSize: isMobile ? '2rem' : '2.75rem',
                fontWeight: '800',
                color: GOLD,
                marginBottom: '0.5rem',
                letterSpacing: '-0.02em',
              }}
            >
              Terms of Service
            </h1>
            <p style={{ color: TEXT_MUTED, marginBottom: '2.5rem', fontSize: '0.9375rem' }}>
              Last updated: {lastUpdated}
            </p>

            {sections.map((section) => (
              <div
                key={section.title}
                style={{
                  backgroundColor: CARD_BG,
                  border: GOLD_BORDER,
                  borderRadius: '1rem',
                  padding: isMobile ? '1.25rem' : '1.75rem',
                  marginBottom: '1.25rem',
                }}
              >
                <h2
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    color: GOLD,
                    marginTop: 0,
                    marginBottom: '0.75rem',
                  }}
                >
                  {section.title}
                </h2>
                <p
                  style={{
                    color: TEXT_BODY,
                    lineHeight: 1.7,
                    margin: 0,
                    fontSize: '0.9375rem',
                  }}
                >
                  {section.content}
                </p>
              </div>
            ))}

            <p style={{ color: TEXT_MUTED, fontSize: '0.875rem', marginTop: '2rem' }}>
              See also our{' '}
              <Link to="/privacy" style={{ color: GOLD }}>
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
